<script lang="ts">
import ConfirmModal from './ConfirmModal.vue';
import AppButton from '@/components/v2/AppButton.vue';
import TextField from '@/components/v2/TextField.vue';
import Alert from '@/components/v2/Alert.vue';
import { Plus } from 'lucide-vue-next';

export default {
  name: 'CreateCarrierModal',
  components: { ConfirmModal, AppButton, TextField, Alert, Plus },
};
</script>

<script setup lang="ts">
import { ref, watch, onMounted, inject, computed } from 'vue';
import { adminService } from '@/services/AdminService';
import { AppState } from '@/store';

const emit = defineEmits(['created']);

const form = ref({ name: '' });
const isDirty = ref(false);
const unwatch = ref<undefined | (() => void)>(undefined);
const isCreating = ref(false);
const showCreateCarrierError = ref(false);
const errorMessage = ref('');
const keepConfirmModalOpen = ref(false);

const nameError = computed(() => {
  if (form.value.name.length > 100) {
    return 'Name must be less than 100 characters';
  }
  return undefined;
});

const store = inject<AppState>('store');

onMounted(() => {
  if (!store) {
    throw new Error('Store not found');
  }
});

unwatch.value = watch(
  form,
  () => {
    isDirty.value = true;
  },
  { deep: true }
);

function resetForm() {
  form.value.name = '';
  isDirty.value = false;
  unwatch.value = undefined;
}

async function confirmAction(): Promise<boolean> {
  isCreating.value = true;
  let isError = false;
  keepConfirmModalOpen.value = false;

  try {
    await adminService.CreateCarrier({ insurerGroupName: form.value.name });
  } catch (error: unknown) {
    const err = error as { cause?: string };
    if (err.cause === 'ErrCarrierNameDuplicate') {
      showCreateCarrierError.value = true;
      errorMessage.value =
        'The inputted carrier already exists. Please enter a new, unique name to create a carrier.';
      keepConfirmModalOpen.value = true;
    } else {
      isError = true;
    }
    console.error(error);
  } finally {
    emit('created', isError);
    isCreating.value = false;
  }

  return !isError;
}

function isDisabled(): boolean {
  return !isDirty.value;
}

function dialogOpened() {
  resetForm();
}

function dialogClosed() {
  if (unwatch.value) {
    unwatch.value();
  }
}
</script>

<template>
  <div>
    <div
      v-if="showCreateCarrierError"
      class="tw:fixed tw:left-1/2 tw:top-4 tw:z-50 tw:-translate-x-1/2 tw:min-w-80 tw:max-w-lg"
    >
      <Alert
        type="error"
        closable
        @close="showCreateCarrierError = false"
      >
        <span data-test="errorMessage">{{ errorMessage }}</span>
      </Alert>
    </div>

    <ConfirmModal
      title="Create Carrier"
      :action="confirmAction"
      :dismiss="resetForm"
      confirmText="Create"
      size="md"
      :submitLoading="isCreating"
      :isConfirmDisabled="isDisabled"
      :keepOpen="keepConfirmModalOpen"
      @open="dialogOpened"
      @closed="dialogClosed"
    >
      <template #button="{ props: slotProps }">
        <AppButton
          data-test="create-carrier-btn"
          primary
          @click="slotProps?.onClick"
        >
          Create Carrier
          <Plus :size="16" class="tw:ml-1" />
        </AppButton>
      </template>
      <div class="tw:flex tw:flex-col tw:gap-4 tw:p-4">
        <p class="tw:m-0">
          Enter the name of a new carrier below. A new carrier and matching issuing company will be
          created. This carrier must not already exist.
        </p>
        <TextField
          v-model="form.name"
          label="Name"
          data-test="full-name-input"
          :error="!!nameError"
          :error-message="nameError"
        />
      </div>
    </ConfirmModal>
  </div>
</template>

